import type { } from '@mui/x-charts/themeAugmentation'
import classNames from 'classnames'
import React, { useMemo } from 'react'
import { useGetCardInvoicesPaidRemains } from '../../../hooks/apolloHooks/useGetCardInvoicesPaidRemains'
import { useGetCustomer } from '../../../hooks/apolloHooks/useGetCustomer'
import { GET_CARD_INVOICES_QUERY } from '../../../integrations/allkort/graphql/gql/CustomerQueries'
import { CardInvoicesCustomerDataResponse, CurrentCustomer } from '../../../integrations/allkort/graphql/interface/Customer'
import { formatSEK } from '../../../utils/formatSEK'
import { getOverdueInvoices } from '../../../utils/invoiceDateUtils'
import FetchingAnimation from '../../elements/FetchingAnimation'
import Link from '../../elements/Link'
import { ArrowRightIcon } from '../../svgs/ExpressonSVGs'

// Grace period in days before an invoice is considered overdue
// An invoice is only overdue if the due date has passed by this many days
const OVERDUE_GRACE_PERIOD_DAYS = 1;

type paymentsBalanceProps = {
    data?: any // TODO: Change this
    customerData: CurrentCustomer
    hasActiveSmartCard: boolean
    hasActiveCreditCard: boolean
}

/**
 * Info about payments for credit card and balance for Smartcard
 *
 * @returns
 */
const PaymentsBalance: React.FC<paymentsBalanceProps> = ({ data, customerData, hasActiveSmartCard, hasActiveCreditCard }) => {
    // Fetch all paid/remains data with auto-pagination
    const { paidRemains, loading: paidRemainsLoading } = useGetCardInvoicesPaidRemains();

    // Fetch recent invoices (30) to get dueDate for overdue calculation
    // We need dueDate which is NOT available in paidRemains
    const invoiceVariables = useMemo(() => ({
        first: null,
        after: null,
        before: null,
        last: 30  // Fetch 30 most recent invoices to capture all unpaid/overdue
    }), []);

    const { data: invoicesData, loading: invoicesLoading } = useGetCustomer<CardInvoicesCustomerDataResponse>(
        GET_CARD_INVOICES_QUERY,
        invoiceVariables,
        { fetchPolicy: 'cache-first' }
    );

    // Helper to check if an invoice is unpaid
    // An invoice is unpaid if: remains > 0 OR remains is null (unknown/unpaid)
    const isInvoiceUnpaid = (inv: any) => {
        return inv.remains === null || (inv.remains && inv.remains > 0);
    };

    // Get unpaid invoices count
    const unpaidInvoices = paidRemains.filter(isInvoiceUnpaid);
    const unpaidInvoicesCount = unpaidInvoices.length;

    const overdueInvoices = getOverdueInvoices(
        unpaidInvoices,
        invoicesData?.currentCustomer?.cardInvoices?.nodes ?? [],
        new Date(),
        OVERDUE_GRACE_PERIOD_DAYS
    );
    const overdueInvoicesCount = overdueInvoices.length;


    return (
        <div className="paymentsbalance_component">
            <div className={classNames(
                "paymentsbalance_component__content",
                { 'one-card-type': hasActiveSmartCard && !hasActiveCreditCard || !hasActiveSmartCard && hasActiveCreditCard }
            )}
            >

                {hasActiveSmartCard && (
                    <div className="card-info-section">
                        <Link
                            className={'card-info-section__smartcard'}
                            ariaLabel={'Visa kontobeked'}
                            href={'/mitt-konto/fakturor-kontobesked#statements'}
                        >
                            <label>
                                Smartkort
                                <span className="usp">
                                    Saldo
                                </span>
                            </label>
                            <span>{formatSEK(customerData.balance.remainingBalance)}</span>
                            <span className="small-label">
                                Disponibelt belopp
                                <ArrowRightIcon width={22} height={22} />
                            </span>
                        </Link>
                    </div>
                )}
                {hasActiveCreditCard && (
                    <div className="card-info-section">
                        <Link
                            className={classNames(
                                'card-info-section__creditcard',
                                { 'company': customerData.isCompany }
                            )}
                            ariaLabel={'Visa fakturor'}
                            href={'/mitt-konto/fakturor-kontobesked#invoices'}
                        >
                            <label>
                                {customerData.isCompany ? 'Företagskort' : 'Tankkort'}
                                <span className="usp">
                                    Faktura
                                </span>
                            </label>
                            <span>
                                {paidRemainsLoading ? (
                                    <FetchingAnimation width={135} height={24} />
                                ) : unpaidInvoicesCount > 0 ? (
                                    `${unpaidInvoicesCount} obetalda`
                                ) : (
                                    'Inget att betala'
                                )}
                            </span>
                            <span className="small-label red">
                                {invoicesLoading ? (
                                    <FetchingAnimation width={90} height={16} />
                                ) : overdueInvoicesCount > 0 ? (
                                    `${overdueInvoicesCount} ${overdueInvoicesCount === 1 ? 'förfallen' : 'förfallna'}`
                                ) : ('')
                                }
                                <ArrowRightIcon width={22} height={22} />
                            </span>
                        </Link>
                    </div>
                )}
            </div>
        </div>
    )
}

export default PaymentsBalance
